import type {
  ApiError,
  CallArgs,
  NetworkError,
  ProcedureInput,
  ProcedureName,
  ProcedureOutput,
} from "@nexys/user-management-client";
import { useCallback, useEffect, useState } from "react";

import { useUmClient } from "../context/auth-context.js";

export interface CallResource<T, E> {
  data: T | undefined;
  error: E | null;
  loading: boolean;
  refresh: () => Promise<void>;
  /** Mutate local state without round-tripping the server — useful for optimistic updates. */
  setData: (next: T | undefined) => void;
}

/**
 * React wrapper around one API procedure. Fetches on mount, exposes
 * { data, error, loading, refresh, setData } and re-fetches when
 * `input` changes (shallow stringify equality).
 *
 *   const tokens = useCall("listApiTokens");
 *   const users = useCall("adminListUsers", { status: "active" });
 */
export const useCall = <K extends ProcedureName>(
  procedure: K,
  ...args: CallArgs<ProcedureInput<K>>
): CallResource<ProcedureOutput<K>, ApiError | NetworkError> => {
  const client = useUmClient();
  const [data, setData] = useState<ProcedureOutput<K> | undefined>(undefined);
  const [error, setError] = useState<ApiError | NetworkError | null>(null);
  const [loading, setLoading] = useState(true);

  // Stable equality on a JSON snapshot of input so callers don't have
  // to memoise the object literal at the call site.
  const key = JSON.stringify(args[0] ?? null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const call = client[procedure] as (
      ...a: CallArgs<ProcedureInput<K>>
    ) => ReturnType<(typeof client)[K]>;
    const res = await call(...args);
    if (res.ok) {
      setData(res.data as ProcedureOutput<K>);
    } else {
      setError(res.error);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, procedure, key]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, error, loading, refresh, setData };
};
